import { ReactNode, useRef } from 'react'
import { useMsg } from '../../../common/localizationUtils'
import { SadStates } from '../../../components/SadStates'
import {
  BoxProps,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Flex,
  Link as ChakraLink,
  Text,
  Skeleton,
  Stack
} from '../../chakra'
import { CardHeading } from '../cards/Cards'
import { NoResults, RefreshError } from '../empty_states/EmptyStates'
import { Link, LinkProps } from '../links/Links'
import { downloadHtmlNodeAsImage } from '../../../dashboards/utils'
import { useDashboardPermissions } from '../../../dashboards/hooks'

export const useWidgetReportUrl = () => {
  const url = '/reporting/new'
  return { url }
}

type FloatingReportLinkProps = Omit<LinkProps, 'href'> & {
  reportKey: string
  children: ReactNode
}

const FloatingReportLink = ({ reportKey, children, ...props }: FloatingReportLinkProps) => {
  const { url: widgetReportUrl } = useWidgetReportUrl()
  const url = `${widgetReportUrl}?&key=${reportKey}`
  return (
    <Link href={url} target="_blank" {...props}>
      {children}
    </Link>
  )
}

type WidgetReportLinkProps = {
  reportKey: string
  reportTitleId: string
}

const WidgetReportLink = ({ reportKey, reportTitleId }: WidgetReportLinkProps) => {
  const msg = useMsg()

  return (
    <FloatingReportLink
      aria-describedby={reportTitleId}
      target="_blank"
      reportKey={reportKey}
      display="inline-flex"
      alignItems="right">
      <>
        <span>{msg('dashboards.chart.view_report.link')}</span>
      </>
    </FloatingReportLink>
  )
}

const SubTitleWrapper = ({ children }: { children: ReactNode }) => (
  <Text fontWeight={400} lineHeight="24px" fontSize="14px">
    {children}
  </Text>
)

type CustomReportLinkProps = {
  url: string
  reportTitleId: string
}

const CustomReportLink = ({ url, reportTitleId }: CustomReportLinkProps) => {
  const msg = useMsg()
  return (
    <ChakraLink
      aria-describedby={reportTitleId}
      target="_blank"
      href={url}
      display="inline-flex"
      alignItems="right"
      textAlign="right">
      <span>{msg('dashboards.chart.view_report.link')}</span>
    </ChakraLink>
  )
}

export type ChartProps = {
  children: ReactNode
  title: string
  subTitle?: string
  isLoading?: boolean
  isError?: boolean
  isFetching?: boolean
  isEmpty?: boolean
  noResultsHeading?: string
  reportKey: string
  customReportUrl?: string
  cardStyle?: BoxProps
}

export const ChartContainer = ({
  children,
  title,
  subTitle,
  customReportUrl,
  reportKey,
  isLoading = true,
  isError = false,
  isFetching = true,
  isEmpty = false,
  noResultsHeading
}: ChartProps) => {
  const { has_access_to_custom_report_templates: hasAccessToReportTemplates, can_export_custom_reports: canExport } =
    useDashboardPermissions() || {}
  const msg = useMsg()

  const containerRef = useRef(null)

  const isValidChart = !isError && !isEmpty
  const showSubtitle = isValidChart && !isLoading && subTitle
  const showInlineFetching = !isLoading && isFetching
  const showCustomReportUrl = !isError && hasAccessToReportTemplates && (customReportUrl || reportKey)
  const reportTitleId = `card-title-${customReportUrl || reportKey}`

  return (
    <Card data-testid={`chart-widget-${title}`}>
      <CardHeader justifyContent="space-between">
        <CardHeading as={Flex} maxWidth="356px" direction={{ base: 'column', lg: 'row' }}>
          <Stack>
            <Text id={reportTitleId}>{title}</Text>
            {showSubtitle && <SubTitleWrapper>{subTitle}</SubTitleWrapper>}
          </Stack>
        </CardHeading>
        {isValidChart && canExport && (
          <Button
            minWidth="125px"
            aria-describedby={reportTitleId}
            variant="secondary"
            size="sm"
            onClick={() => {
              downloadHtmlNodeAsImage(containerRef.current, title)
            }}>
            {msg('dashboards.chart.download.label')}
          </Button>
        )}
      </CardHeader>
      <CardBody
        backgroundColor="white"
        ref={containerRef}
        height={400}
        padding={4}
        flex="none"
        position="relative"
        opacity={showInlineFetching ? 0.5 : 1}>
        <SadStates
          states={[
            {
              when: isLoading,
              render: () => <Skeleton width="100%" height="50%" />
            },
            {
              when: isError,
              render: () => (
                <Flex direction="column" height="100%" justifyContent="center" alignItems="center">
                  <RefreshError
                    title={msg('common.ui.error_processing_request_try_again')}
                    graphicHeight="180px"
                    textAlign="center"
                  />
                </Flex>
              )
            },
            {
              when: isEmpty,
              render: () => (
                <Flex direction="column" height="100%" justifyContent="center" alignItems="center">
                  <NoResults
                    title={noResultsHeading || msg('common.ui.no_results_found')}
                    graphicHeight="180px"
                    textAlign="center"
                  />
                  <Text textStyle="bodySemi" textAlign="center">
                    {msg('common.ui.adjust_filters')}
                  </Text>
                </Flex>
              )
            }
          ]}>
          {children}
        </SadStates>
      </CardBody>
      {showCustomReportUrl && (
        <CardFooter justifyContent="right">
          {customReportUrl ? (
            <CustomReportLink url={customReportUrl} reportTitleId={reportTitleId} />
          ) : (
            <WidgetReportLink reportKey={reportKey} reportTitleId={reportTitleId} />
          )}
        </CardFooter>
      )}
    </Card>
  )
}
